import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { getCurrentScreenType, getCompatibleScreens, getCompatibleWorkflows } from 'Helper/screenTypeHelpers'
import StandardModalContentNew from 'GeneralUI/StandardModalContent/StandardModalContentNew.jsx'
import StandardModalContent from 'GeneralUI/StandardModalContent/StandardModalContent.jsx'
import withWindowScreenWorkflowConfigs from './withWindowScreenWorkflowConfigs.js'
import InputGroupText from 'GeneralUI/inputGroupText/InputGroupText.jsx'
import { encodeItemURIComponents } from '../../Helper/routeHelpers.js'
import { pathValidator } from '../../Helper/sysAdminValidators.js'
import { getErrorModalAction } from './DesignerHelpers.jsx'
import { getContextName, push } from 'Helper/routeHelpers'
import { useSelector, useDispatch } from 'react-redux'
import { openModal } from 'actions/modalActions'
import { useLocation } from 'react-router-dom'
import { xtractAPI } from 'actions/apiActions'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import _ from 'lodash'

const ConfigureStepModal = (props) => {
  const {
    isEdit,
    workflowConfig,
    closeSelf,
    startSpinner,
    endSpinnerWithError,
    endSpinnerWithSuccess,
    contextScreenConfigs,
    contextWorkflowConfigs,
    contextWorkflowConfigsId,
    selectedStepIndex = -1
  } = props

  const dispatch = useDispatch()

  const { pathname } = useLocation()
  const currentWorkflowPath = useSelector(state => state.router?.match?.params?.workflow)
  const currentScreenType = getCurrentScreenType()

  const [screenState, setScreenState] = useState("")
  const [titleState, setTitleState] = useState("")

  const currentWorkflowConfig = useMemo(() => {
    return contextWorkflowConfigs.find(workflow => workflow.path === currentWorkflowPath && workflow.screenType === currentScreenType)
  }, [currentWorkflowPath, contextWorkflowConfigs, currentScreenType])

  const maxStepIndex = useMemo(() => {
    return (workflowConfig?.sequence || []).length - 1
  }, [workflowConfig])

  const currentStepConfig = useMemo(() => {
    const steps = currentWorkflowConfig?.sequence ? currentWorkflowConfig.sequence : []
    if (isEdit && steps[selectedStepIndex]) {
      return steps[selectedStepIndex]
    } else {
      return { name: "", screenPath: "" }
    }
  }, [selectedStepIndex, currentWorkflowConfig, currentScreenType])

  const nurseDashboardScreens = useSelector(state => state.appConfig?.nurseDashboard?.screens) || []
  const adminDisplayScreens = useSelector(state => state.appConfig?.adminDisplay?.screens) || []
  const adminDisplayWorkflows = useSelector(state => state.appConfig?.adminDisplay?.workflows) || []
  const patientDisplayScreens = useSelector(state => state.appConfig?.patientDisplay?.screens) || []
  const patientDisplayWorkflows = useSelector(state => state.appConfig?.patientDisplay?.workflows) || []

  useEffect(() => {
    if (currentStepConfig) {
      setTitleState(currentStepConfig.name)
      setScreenState(currentStepConfig.screenPath || currentStepConfig.url)
    }
  }, [currentStepConfig])

  const screenType = useMemo(getCurrentScreenType, [])

  const screenContext = useMemo(() => {
    return getContextName(pathname)
  }, [pathname])

  const availableScreensRefs = useMemo(() => {
    // all screens on the nurseDashboard compatible with this screen type
    const compatibleDashboardScreens = getCompatibleScreens(screenType, nurseDashboardScreens)
      .map(ds => ({ label: '(dashboard screen ending) ' + ds.name, value: 'dashboard/screen/' + ds.path }))
      .sort()

    // all screens on the adminDashbaord compatible with this screen type
    const compatibleAdminScreens = getCompatibleScreens(screenType, adminDisplayScreens)
      .map(ds => ({ label: '(admin screen ending) ' + ds.name, value: 'dashboard/admin/screen/' + ds.path }))
      .sort()

    // all workflows on the adminDashbaord compatible with this screen type
    const compatibleAdminWorkflows = getCompatibleWorkflows(screenType, adminDisplayWorkflows)
      .map(ds => ({ label: '(admin workflow ending) ' + ds.name, value: 'dashboard/admin/workflow/' + ds.path }))
      .sort()

    const compatibleContextScreens = getCompatibleScreens(screenType, contextScreenConfigs)
      .map(screen => ({label: screen.name, value: screen.path}))
      .sort()

    const contextNameShort = screenContext === 'patientDisplay' ? 'patient' : (screenContext === 'nurseDashbaord' ? 'dashboard' : 'admin')
    const contextWorkflowPrefix = contextNameShort === 'patient' ? 'dashboard/patient/:patientId/workflow' : (contextNameShort === 'dashboard' ? `${contextNameShort}/workflow` : `dashboard/${contextNameShort}/workflow`)
    const compatibleContextWorkflows = getCompatibleWorkflows(screenType, contextWorkflowConfigs)
      .map(workflow => ({label: `(${contextNameShort} workflow ending) ${workflow.name}`, value: `${contextWorkflowPrefix}/${workflow.path}`}))
      .sort()
      
    let availableRefs = compatibleContextScreens || []
      
    const allowEndings = selectedStepIndex >= 0 && selectedStepIndex === maxStepIndex
    if(!allowEndings) {
      return availableRefs
    }

    // nurseDashboard doesn't support workflows so its not account for here
    // admin display can return to any nurseDashboard screen as an ending step
    // patient display can return to any admin or nurse dashboard screen as well as any admin workflow as an ending step
    switch(screenContext) {
      case 'patientDisplay':
        return availableRefs.concat(compatibleContextWorkflows)
          .concat([{ label: '(patient dashboard ending) patient display', value: 'dashboard/patient/:patientId' }])
          .concat(compatibleDashboardScreens)
          .concat(compatibleAdminScreens)
          .concat(compatibleAdminWorkflows)
      case 'adminDisplay':
        return availableRefs.concat(compatibleContextWorkflows)
          .concat(compatibleDashboardScreens)
      default:
        return []
    }
  }, [selectedStepIndex, nurseDashboardScreens, adminDisplayScreens, adminDisplayWorkflows, patientDisplayScreens, patientDisplayWorkflows, maxStepIndex, contextScreenConfigs, contextWorkflowConfigs, currentScreenType])

  const screenSelect = useCallback(() => {
    return (
      <div className='screen-input'>
        <InputGroupText
          type="select"
          title="Screen"
          options={availableScreensRefs}
          value={screenState}
          aria-label={'Screen'}
          onChange={e => setScreenState(e.value)}
        />
      </div>
    )
  }, [isEdit, availableScreensRefs, screenState, setScreenState])

  const titleInput = useCallback(() => {
    return (
      <div className='title-input'>
        <InputGroupText
          type="text"
          title="Title"
          placeholder=""
          value={titleState}
          aria-label={'screen title'}
          style={{ textAlign: 'left' }}
          onChange={e => setTitleState(e.target.value)}
        />
      </div>
    )
  }, [titleState, setTitleState])

  const validateWorkflows = useCallback((workflows) => {
    if (workflows.some(workflow => {
      return !workflow.name || !workflow.path
    })) {
      const ErrorContent = <div>
        {"Each configured workflow must have a title and a URL name."}
      </div>
      dispatch(getErrorModalAction('Validation error', ErrorContent))
      return false
    }
    const validationErrors = pathValidator(workflows, 'workflow')
    if (validationErrors) {
      const ErrorContent = <div>
        {"Each configured workflow must have a unique path. The following errors were detected:"}
        <ul>
          {validationErrors}
        </ul>
      </div>
      errorMessage('Validation error', ErrorContent)
      return false
    } else {
      // no errors to handle, we passed
      return true
    }
  })

  const saveWorkflow = useCallback(() => {
    let updatedWorkflow
    // Update edited step
    const data = contextWorkflowConfigs.map(w => {
      let workflow = _.cloneDeep(w)
      if ((!workflow.screenType || workflow.screenType === currentScreenType) && workflow.path === currentWorkflowConfig.path && workflow.sequence) {
        if (isEdit && selectedStepIndex !== null) {
          if (screenState.indexOf('/') === -1) {
            // if url was defined, remove it in favor of screenPath
            delete workflow.sequence[selectedStepIndex].url
            workflow.sequence[selectedStepIndex].screenPath = screenState
          } else {
            // if screenPath was defined, remove it in favor of url
            delete workflow.sequence[selectedStepIndex].screenPath
            workflow.sequence[selectedStepIndex].url = screenState
          }
          workflow.sequence[selectedStepIndex].name = titleState
        } else {
          const obj = { name: titleState }
          if (screenState.indexOf('/') === -1) {
            obj.screenPath = screenState
          } else {
            obj.url = screenState
          }
          workflow.sequence.push(obj)
        }
        updatedWorkflow = workflow
      }
      return workflow
    })

    if (validateWorkflows(data)) {
      const callbacks = {
        200: response => {
          endSpinnerWithSuccess()
          const split = pathname.split('/step')
          const nextStep = selectedStepIndex + 2; // 1 because index is 0 based and 1 to progress
          if (nextStep <= updatedWorkflow.sequence.length + 1 && !isEdit) { // only progress if we are adding steps, not editing.
            // make sure we don't progress if we are already on the max step
            push(`${split[0]}/step/${nextStep}`)
          }
        },
        400: (response, action) => endSpinnerWithError({ action, response, errors: response.data.errors, }),
        error: (response, action) => endSpinnerWithError({ response, action })
      }
      startSpinner()
      dispatch(xtractAPI('config/' + contextWorkflowConfigsId, 'update', { value: JSON.stringify(data.map(encodeItemURIComponents)) }, callbacks))
    }
  }, [contextWorkflowConfigs, contextWorkflowConfigsId, currentScreenType, screenState, titleState, validateWorkflows, currentWorkflowConfig, selectedStepIndex, pathname, encodeItemURIComponents])

  const confirmDeleteModal = useCallback((callback) => {
    dispatch(openModal('confirm_modal', {
      title: 'Delete Step',
      content: (
        <StandardModalContent
          messageContent={
            <div style={{ margin: '25px' }}>
              Are you sure you want to delete <b>{currentStepConfig?.name ? currentStepConfig?.name : ''} </b>step?
            </div>
          }
          cancelText={'No'}
          cancelHandler={() => false}
          okText={'Yes'}
          okHandler={callback}
        />
      ),
      closeable: true
    }))
  }, [currentStepConfig])

  const deleteStep = useCallback(() => {
    let updatedWorkflow
    // Delete selected Step
    const data = contextWorkflowConfigs.map(w => {
      let workflow = _.cloneDeep(w)
      if ((!workflow.screenType || workflow.screenType === currentScreenType) && workflow.path === currentWorkflowConfig.path && workflow.sequence) {
        workflow.sequence = workflow.sequence.filter((step, i) => i !== selectedStepIndex)
        updatedWorkflow = workflow
      }
      return workflow
    })

    if (validateWorkflows(data)) {
      const callbacks = {
        200: response => {
          endSpinnerWithSuccess()
          const gotoIndex = selectedStepIndex // go to previous step = +1 since index is 0 based -1 to go back
          if (gotoIndex <= 0 && updatedWorkflow.sequence.length === 0) {
            // no step to go to so go to empty workflow
            const workflowSplit = pathname.split('/workflow')
            push(workflowSplit[0])
          } else if (gotoIndex <= 0) {
            // we cant go down a step but we can stay on the current step as the next greater
            // step shifts down to the current position
            const stepSplit = pathname.split('/step')
            push(`${stepSplit[0]}/step/${selectedStepIndex + 1}`) // +1 because index is 0 based
          } else {
            const stepSplit = pathname.split('/step')
            push(`${stepSplit[0]}/step/${gotoIndex}`)
          }
        },
        400: (response, action) => endSpinnerWithError({ action, response, errors: response.data.errors, }),
        error: (response, action) => endSpinnerWithError({ response, action })
      }
      startSpinner()
      dispatch(xtractAPI('config/' + contextWorkflowConfigsId, 'update', { value: JSON.stringify(data.map(encodeItemURIComponents)) }, callbacks))
    }
  }, [contextWorkflowConfigs, contextWorkflowConfigsId, currentScreenType, screenState, titleState, validateWorkflows, currentWorkflowConfig, selectedStepIndex, pathname, encodeItemURIComponents])

  return (
    <StandardModalContentNew
      cancelHandler={closeSelf}
      okHandler={isEdit && maxStepIndex !== 0 ? () => confirmDeleteModal(deleteStep) : null}
      thirdHandler={saveWorkflow}
      okText={'Delete'}
      okClassName={'btn-warning'}
      thirdText={'Save'}
      cancelText={'Cancel'}
      closeSelf={closeSelf}
    >
      <div className='configure-step-modal'>
        {screenSelect()}
        {titleInput()}
      </div>
    </StandardModalContentNew>
  )
}

ConfigureStepModal.propTypes = {
  closeSelf: PropTypes.func.isRequired,
}

const enhance = compose(
  withWindowScreenWorkflowConfigs()
)

export default enhance(ConfigureStepModal)